import React, { Component } from "react";
import Palette from "./Palette";
import seedColors from "./seedColors";
import { generatePalette } from "./colorHelpers";
import { Route, Switch, Redirect } from "react-router-dom";
import PaletteList from "./PaletteList";
import SingleColorPalette from "./SingleColorPalette";
import Generator from './Generator';
import Explore from './Explore';
import ContrastChecker from './ContrastChecker';
import GlobalNav from './components/GlobalNav';
import ShareHandler from './components/ShareHandler';
import { ColorSelectionProvider } from './contexts/ColorSelectionContext';
import { ThemeContextProvider, ThemeContext } from './contexts/ThemeContext';
import { ThemeProvider } from '@material-ui/core/styles';
import themes from './styles/themes';
import CssBaseline from '@material-ui/core/CssBaseline';

// We have defined our routes in the App component

function loadJSON(key, fallback) {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch (e) {
    console.error(`Failed to load ${key} from localStorage`, e);
  }
  return fallback;
}

// One-time, idempotent migration from the legacy merged `palettes` key.
// Only palettes whose id is NOT a built-in preset are treated as user-created;
// presets are never migrated. Guarded by a flag so it never runs twice.
function migrateLegacyPalettes(existingUser) {
  try {
    if (window.localStorage.getItem("userPalettesMigrated")) return existingUser;
    const raw = window.localStorage.getItem("palettes");
    window.localStorage.setItem("userPalettesMigrated", "1");
    if (!raw) return existingUser;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return existingUser;

    const seedIds = new Set(seedColors.map(p => p.id));
    const existingIds = new Set(existingUser.map(p => p.id));
    const recovered = parsed.filter(
      p => p && p.id && !seedIds.has(p.id) && !existingIds.has(p.id)
    );
    if (recovered.length === 0) return existingUser;

    const merged = [...recovered, ...existingUser];
    window.localStorage.setItem("userPalettes", JSON.stringify(merged));
    return merged;
  } catch (e) {
    console.error("Legacy palette migration failed", e);
    return existingUser;
  }
}

class App extends Component {
  constructor(props) {
    super(props);
    this.state = {
      // User-created palettes are stored separately from the immutable seed
      // presets so presets can never be edited or deleted.
      userPalettes: migrateLegacyPalettes(loadJSON("userPalettes", [])),
      projects: loadJSON("projects", []),
      collections: loadJSON("collections", []),
    };
    this.savePalette = this.savePalette.bind(this);
    this.updatePalette = this.updatePalette.bind(this);
    this.findPalette = this.findPalette.bind(this);
    this.deletePalette = this.deletePalette.bind(this);
    this.addProject = this.addProject.bind(this);
    this.addCollection = this.addCollection.bind(this);
  }

  // All palettes available for lookup: the user's first, then the presets.
  allPalettes() {
    return [...this.state.userPalettes, ...seedColors];
  }

  findPalette(id) {
    return this.allPalettes().find(palette => palette.id === id);
  }

  isUserPalette(id) {
    return this.state.userPalettes.some(p => p.id === id);
  }

  persist(key, value) {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      console.error(`Failed to persist ${key}`, e);
    }
  }

  savePalette(newPalette) {
    this.setState(
      st => ({ userPalettes: [newPalette, ...st.userPalettes] }),
      () => this.persist("userPalettes", this.state.userPalettes)
    );
  }

  updatePalette(id, updatedPalette) {
    // Only user palettes are editable; presets are immutable.
    this.setState(
      st => ({
        userPalettes: st.userPalettes.map(p => (p.id === id ? updatedPalette : p)),
      }),
      () => this.persist("userPalettes", this.state.userPalettes)
    );
  }

  deletePalette(id) {
    // Presets are protected: only user palettes can be removed.
    this.setState(
      st => ({ userPalettes: st.userPalettes.filter(p => p.id !== id) }),
      () => this.persist("userPalettes", this.state.userPalettes)
    );
  }

  addProject(name) {
    const project = { id: `project-${Date.now()}`, name };
    this.setState(
      st => ({ projects: [...st.projects, project] }),
      () => this.persist("projects", this.state.projects)
    );
    return project;
  }

  addCollection(name) {
    const collection = { id: `collection-${Date.now()}`, name };
    this.setState(
      st => ({ collections: [...st.collections, collection] }),
      () => this.persist("collections", this.state.collections)
    );
    return collection;
  }

  render() {
    const { userPalettes, projects, collections } = this.state;
    return (
      <ThemeContextProvider>
        <ThemeContext.Consumer>
          {({ themeName }) => (
            <ThemeProvider theme={themes[themeName] || themes['bauhaus']}>
              <CssBaseline />
              <ColorSelectionProvider>
                <Switch>
          <Route
            exact
            path={["/generate", "/generate/:hexString"]}
            render={(routeProps) => (
              <Generator
                savePalette={this.savePalette}
                updatePalette={this.updatePalette}
                userPalettes={userPalettes}
                projects={projects}
                collections={collections}
                addProject={this.addProject}
                addCollection={this.addCollection}
                {...routeProps}
              />
            )}
          />
          <Route
            exact
            path={["/explore", "/explore/:category"]}
            render={(routeProps) => <Explore {...routeProps} />}
          />
          <Route
            exact
            path="/contrast-checker"
            render={() => (
              <>
                <GlobalNav />
                <div style={{ padding: '1rem' }}>
                  <ContrastChecker />
                </div>
              </>
            )}
          />
          {/* The builder is now the generator itself. */}
          <Route exact path="/palette/new" render={() => <Redirect to="/generate" />} />
          <Route
            exact
            path="/palette/share/:encodedData"
            render={(routeProps) => (
              <ShareHandler
                savePalette={this.savePalette}
                palettes={this.allPalettes()}
                {...routeProps}
              />
            )}
          />
          <Route
            exact
            path="/palette/:paletteId/:colorId"
            render={(routeProps) => (
              <SingleColorPalette
                colorId={routeProps.match.params.colorId}
                palette={generatePalette(
                  this.findPalette(routeProps.match.params.paletteId)
                )}
              />
            )}
          />
          <Route
            exact
            path="/"
            render={(routeProps) => (
              <PaletteList
                userPalettes={userPalettes}
                seedPalettes={seedColors}
                projects={projects}
                collections={collections}
                deletePalette={this.deletePalette}
                {...routeProps}
              />
            )}
          />
          <Route
            exact
            path="/palette/:id"
            render={(routeProps) => (
              <Palette
                palette={generatePalette(
                  this.findPalette(routeProps.match.params.id)
                )}
              />
            )}
          />
                </Switch>
              </ColorSelectionProvider>
            </ThemeProvider>
          )}
        </ThemeContext.Consumer>
      </ThemeContextProvider>
    );
  }
}

export default App;
